<template>
    <div class="position-relative">
        <div class="active-chat-asset-preview-container position-absolute">
            <active-chat-asset-preview :files="files" :is-loading-files="isLoadingFiles" />
        </div>

        <active-chat-templates
            :active-chat="activeChat"
            :active-chat-templates-tab.sync="activeChatTemplatesTab"
            :channels="channels"
            :selected-template.sync="selectedTemplate"
            @reset-template="resetTemplate"
            @set-quick-reply="setChatInputQuickReply"
            @set-template="setChatInputTemplate"
        />

        <div ref="dropZoneRef" class="chat-app-form-wrapper px-1">
            <div v-if="isOverDropZone" class="chat-app-form round-large p-0">
                <div class="shadow round-large dropzone">
                    <p class="mt-0 mb-0">
                        <font-awesome-icon class="text-primary" icon="files" size="xl" />
                        <span class="ml-1 title font-weight-bold">
                            {{ $t("upload.upload-your-files") }}
                        </span>
                        <br />
                        <span class="subtitle">{{ $t("upload.drop-to-upload") }}</span>
                    </p>
                </div>
            </div>
            <b-form
                v-show="!isOverDropZone"
                :class="{ 'd-flex': !isOverDropZone }"
                class="pt-1 pb-1 h-auto chat-app-form w-100 px-2 justify-content-between shadow round-large"
            >
                <b-dropdown id="compose-options" dropup no-caret variant="primary">
                    <span slot="button-content">
                        <font-awesome-icon icon="plus" size="xs" />
                    </span>

                    <b-dropdown-item-button
                        v-b-tooltip.hover.bottom="
                            (!isChatAssignedToMeV21(activeChat.lastConversation.assignedUserId) &&
                                shouldAssignV21(activeChat.channel)) ||
                            !resolveCustomerCareWindow
                                ? $t('customer-care-window-closed')
                                : ''
                        "
                        :disabled="
                            (!isChatAssignedToMeV21(activeChat.lastConversation.assignedUserId) &&
                                shouldAssignV21(activeChat.channel)) ||
                            !resolveCustomerCareWindow
                        "
                        @click="activeChatTemplatesTab = 'quickReplies'"
                    >
                        <font-awesome-icon class="mr-1" icon="slash-forward" />
                        {{ $t("entities.quick-replies.Quick replies") }}
                    </b-dropdown-item-button>

                    <b-dropdown-item-button
                        v-if="activeChat.channel.platform === 'WHATSAPP'"
                        :disabled="
                            !isChatAssignedToMeV21(activeChat.lastConversation.assignedUserId) &&
                            shouldAssignV21(activeChat.channel)
                        "
                        @click="activeChatTemplatesTab = 'approvedTemplates'"
                    >
                        <font-awesome-icon :icon="['fab', 'whatsapp']" class="mr-1" />
                        {{ $t("platforms.WHATSAPP") }} {{ $t("entities.templates.approved-templates") }}
                    </b-dropdown-item-button>

                    <b-dropdown-item-button
                        v-b-tooltip.hover.bottom="
                            activeChat.channel.platform.toUpperCase() === 'SMS' ? $t('sms-no-assets-allowed') : ''
                        "
                        :disabled="
                            (!isChatAssignedToMeV21(activeChat.lastConversation.assignedUserId) &&
                                shouldAssignV21(activeChat.channel)) ||
                            activeChat.channel.platform.toUpperCase() === 'SMS'
                        "
                        @click="openFilePrompt()"
                    >
                        <b-form-file
                            v-show="false"
                            id="files"
                            :accept="allowedFileTypes"
                            multiple
                            @input="$emit('upload-files', $event)"
                        />

                        <font-awesome-icon class="mr-1" icon="paperclip" />
                        {{ $t("Attachments") }}
                    </b-dropdown-item-button>
                </b-dropdown>

                <div class="d-inline-flex flex-column flex-grow-1">
                    <div v-if="activeReply" id="active-reply" class="position-relative mx-1">
                        <entry-reply-component
                            :key="activeReply.id"
                            :contact="activeChat.contact"
                            :conversation-entries="activeChat.lastConversation.entries"
                            :reply-to-message-id="activeReply.id"
                        />
                        <b-button
                            class="rounded-circle p-0 bg-white position-absolute d-block"
                            variant="flat-secondary"
                            @click="removeReply(activeChat.id)"
                        >
                            <font-awesome-icon icon="times" size="sm" />
                        </b-button>
                    </div>

                    <div
                        v-show="showAutomaticSignature('prepend-agent-name-bold')"
                        id="signature"
                        class="text-truncate ml-1 font-weight-bold"
                    >
                        {{ getUserData().displayName }}
                    </div>

                    <b-input-group class="input-group-merge form-send-message">
                        <b-form-textarea
                            ref="writeMessage"
                            v-model="chatEntryInput.message"
                            :class="{ 'bg-white': !isLoadingCompose }"
                            :disabled="
                                (!isChatAssignedToMeV21(activeChat.lastConversation.assignedUserId) &&
                                    shouldAssignV21(activeChat.channel)) ||
                                isLoadingCompose
                            "
                            :maxlength="resolveWriteMessageMaxChars"
                            :placeholder="$t('inbox.write-message-placeholder')"
                            :readonly="isLoadingCompose"
                            autocomplete="off"
                            class="overflow-auto border-0"
                            max-rows="26"
                            no-resize
                            rows="2"
                            size="sm"
                            @blur="
                                setStoredMessages({
                                    chatId: activeChat.lastConversation.id,
                                    inboxModuleType,
                                    message: chatEntryInput.message,
                                })
                            "
                            @input="onTyping"
                            @paste="uploadFile"
                            @keydown.native.191="activeChatTemplatesTab = 'quickReplies'"
                            @keydown.native.111="activeChatTemplatesTab = 'quickReplies'"
                            @keydown.native.enter.exact.prevent="checkEnterToSend"
                            @keydown.native.enter.ctrl.exact="checkShiftEnterToSend"
                            @keydown.native.ctrl.37.prevent="activeTabIndex--"
                            @keydown.native.ctrl.39.prevent="activeTabIndex++"
                        />
                    </b-input-group>

                    <div v-show="showAutomaticSignature('append-agent-name')" id="signature" class="text-truncate ml-1">
                        {{ getUserData().displayName }}
                    </div>
                </div>

                <div v-show="shallShowEmojiPicker" id="container-emojipicker">
                    <VEmojiPicker @select="selectEmoji" />
                </div>

                <b-button id="toggle-chatgpt-button" v-b-modal:chat-gpt-modal>
                    <img alt="ChatGPT" src="~@/assets/images/icons/chatgpt_logo.png" />
                </b-button>

                <div class="d-inline-block">
                    <b-button
                        v-show="!isMobile()"
                        :disabled="!isEnabled"
                        type="button"
                        variant="flat-secondary"
                        @click="shallShowEmojiPicker = !shallShowEmojiPicker"
                    >
                        <font-awesome-icon
                            :icon="['fa', 'laugh']"
                            class="cursor-pointer text-secondary-tint"
                            size="xl"
                        />
                    </b-button>
                </div>

                <b-button
                    id="send-message"
                    :disabled="
                        (!isEnabled && !isTemplate) ||
                        (isTemplate && selectedTemplate.mediaType && !hasMediaTemplateFilesUploaded) ||
                        isSendPending
                    "
                    class="round-medium flex-shrink-0"
                    type="button"
                    variant="primary"
                    @click="$emit('send-message')"
                >
                    <b-spinner v-if="isSendPending" small></b-spinner>
                    <font-awesome-icon v-else :icon="['fa', 'paper-plane-top']" class="cursor-pointer" size="sm" />
                </b-button>
            </b-form>
        </div>
    </div>
</template>

<script>
import { getUserData } from "@/auth/getUserData";
import useActiveChatTemplates from "@/composables/useActiveChatTemplates";
import useAppConfig from "@/composables/useAppConfig";
import useFileUploads from "@/composables/useFileUploads";
import useTemplates from "@/composables/useTemplates";

//import EntryReplyComponent from "@/views/modules/messaging-inbox/active-chat-log-entries/entry-components/reply.vue";
import EntryReplyComponent from "@/views/modules/messaging-inbox/chat-log-entries/components/reply.vue";

// Composables v2.1
import useChannels from "@/composables/v2.1/useChannels";
import { useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import useStoredMessages from "@/composables/v2.1/useStoredMessages";
import toastificationContent from "@/layouts/components/toastification/toastificationContent";
import i18n from "@/libs/i18n";
import { InboxModuleType } from "@/types/inboxModule.type";
import { getChannelSettingValue, resolveWriteMessageMaxChars } from "@/utils/channels";
import {
    isChatAssignedToMeV21,
    isChatEnabledV21,
    resolveRemainingCustomerCareWindow,
    shouldAssignV21,
} from "@/utils/chats";
import { isMobile } from "@/utils/mobile";
import ActiveChatTemplates from "@/views/components/inbox/active-chat-templates/activeChatTemplates.vue";
import { computed, onMounted, onUnmounted, ref, watch } from "@vue/composition-api";
import { useDropZone, useFocus } from "@vueuse/core";
import {
    BButton,
    BCol,
    BDropdown,
    BDropdownItemButton,
    BForm,
    BFormFile,
    BFormInput,
    BFormTextarea,
    BInputGroup,
    BRow,
    BSpinner,
    VBTooltip,
} from "bootstrap-vue";

import { escapeRegExp } from "lodash";
import { VEmojiPicker } from "v-emoji-picker";
import { useToast } from "vue-toastification/composition";
import activeChatAssetPreview from "../active-chat/activeChatAssetPreview";
import useActiveChat from "@/composables/useActiveChat";
import useReplies from "@/composables/v2.1/useReplies";

export default {
    components: {
        BRow,
        BCol,
        BButton,
        BDropdown,
        BDropdownItemButton,
        BForm,
        BFormFile,
        BFormInput,
        BFormTextarea,
        BInputGroup,
        BSpinner,
        VBTooltip,

        ActiveChatTemplates,
        activeChatAssetPreview,
        VEmojiPicker,
        EntryReplyComponent,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    props: {
        activeChat: {
            type: Object,
            required: true,
        },
        chatEntryInput: {
            type: Object,
            required: true,
        },
        isSendPending: {
            type: Boolean,
            required: true,
        },
        files: {
            type: Array,
            required: false,
        },
        typingInterval: {
            type: Number | Boolean,
            required: true,
        },
        isLoadingCompose: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            selectedQuickReplyString: null,
        };
    },
    methods: {
        // @NOTICE copy-pasted from frontend v1
        selectEmoji(emoji) {
            // 1. Get the message element
            const messageElement = this.$refs["writeMessage"];

            // 2. Set the element in focus
            messageElement.focus();

            // 3. Get location of the caret
            const caretLocation = messageElement.selectionStart;

            // 4. Place emoji in location of caret
            this.chatEntryInput.message = `${this.chatEntryInput.message.slice(0, caretLocation)}${
                emoji.data
            }${this.chatEntryInput.message.slice(caretLocation)}`;

            // 5. Place caret after newly placed emoji (since some emojis are longer than 1 char we need to add emoji.length)
            const prevMessage = this.chatEntryInput.message;

            this.$nextTick(() => {
                let pos = caretLocation + emoji.data.length;

                // if message changes since last tick add difference in lengt to pos
                if (prevMessage !== this.message) {
                    pos += this.chatEntryInput.message.length - prevMessage.length;
                }

                this.$refs["writeMessage"].setSelectionRange(pos, pos);
            });

            this.shallShowEmojiPicker = false;
        },
        setChatInputQuickReply({ content }) {
            this.$emit("set-chat-entry-input", { ...this.chatEntryInput, message: content });

            this.isQuickReply = true;
            this.activeChatTemplatesTab = "";
        },
        setChatInputTemplate(template) {
            this.$emit("set-chat-entry-input", { ...this.chatEntryInput, message: template.content });

            this.isTemplate = true;
            this.activeChatTemplatesTab = "";
            this.clearFiles();

            // Media templates must have an attachment before we can send them
            if (template.mediaType) {
                if (!template.assetId) {
                    this.toast({
                        component: toastificationContent,
                        props: {
                            title: i18n.t("entities.templates.approved-media-templates"),
                            text: i18n.t("entities.templates.media-no-file"),
                            icon: "exclamation-triangle",
                            variant: "warning",
                        },
                    });

                    return;
                }

                this.addAsset(template.assetId);
                this.hasMediaTemplateFilesUploaded = true;
            }
        },
        // We need this method, because the b-form-component cannot be used with buttons out-of-the-box
        openFilePrompt() {
            document.getElementById("files").click();
        },
    },
    setup(props, { emit }) {
        const { clearFiles, addAsset, getAllowedFileTypes } = useFileUploads();

        /**
         * CHANNELS
         */
        const { channels } = useChannels();

        /***/
        const { enterToSend } = useAppConfig();

        const shallShowEmojiPicker = ref(false);
        const writeMessage = ref();
        const isTypingTimeout = ref();

        // Drag and drop files to upload.
        const dropZoneRef = ref(null);
        const onFileDrop = (files) => {
            emit("upload-files", files);
        };
        const { isOverDropZone } = useDropZone(dropZoneRef, onFileDrop);

        const { templateSearchString, templates, fetchTemplates } = useTemplates();
        const toast = useToast();

        const {
            activeTabIndex,
            activeChatTemplatesTab,
            resetTemplate,
            selectedTemplate,
            isTemplate,
            isQuickReply,
            hasMediaTemplateFilesUploaded,
        } = useActiveChatTemplates(props.activeChat);

        let lastSelectedTemplate = ref({});

        // If a message has already been set, check if that is a template
        if (props.chatEntryInput.message) {
            fetchTemplates({
                params: {
                    join: "channels||id",
                    fields: "title,content,mediaType,components",
                    filter: [`channels.id||$eq||${props.activeChat.channel.id}`],
                },
            }).then(() => {
                const matchedTemplate = templates.value.find((template) => {
                    const baseRegexString = template.content.replace(/[-\/\\^$*+?.()|[\]]/g, "\\$&");
                    const regex = new RegExp(baseRegexString.replace(/{{\s*([^}]*)\s*}}/g, ".*"));
                    return regex.test(props.chatEntryInput.message);
                });

                if (matchedTemplate) {
                    selectedTemplate.value = matchedTemplate;
                    isTemplate.value = true;
                }
            });
        }

        const { inboxModuleType } = useInboxModuleType();
        const startedWebsocketsPayload = computed(() => ({
            type: "startTyping",
            payload: {
                inboxModuleType: inboxModuleType.value,
                chatId: props.activeChat.lastConversation.id,
                userId: getUserData().id,
                userName: getUserData().name,
                typingTime: new Date(),
                direction: "out",
                type: "messenger",
            },
        }));

        const stoppedWebsocketsPayload = computed(() => ({
            type: "stoppedTyping",
            payload: {
                inboxModuleType: inboxModuleType.value,
                userId: getUserData().id,
                userName: getUserData().name,
            },
        }));

        const { getActiveChat } = useActiveChat();
        watch(getActiveChat, () => {
            clearTimeout(isTypingTimeout.value);
            clearInterval(props.typingInterval);
            emit("set-typing-interval", false);
            emit("send-websockets-message", stoppedWebsocketsPayload.value);
        });

        const templateRegex = /^\/.*/;

        onMounted(() => {
            clearFiles();
        });

        onUnmounted(() => {
            clearTimeout(isTypingTimeout.value);
            clearInterval(props.typingInterval);
            emit("set-typing-interval", false);
            emit("send-websockets-message", stoppedWebsocketsPayload.value);
        });

        const lastConversationId = computed(() => props.activeChat.lastConversation?.id);
        watch(lastConversationId, () => useFocus(writeMessage, { initialValue: true }));

        const setTyping = (event) => {
            if (!templateRegex.test(props.chatEntryInput.message)) {
                activeChatTemplatesTab.value = "";
            }

            if ([13, 0, 9, 18, 16].includes(event.keyCode)) {
                return;
            }

            // We keep track of the last selected template for future use
            // For instance checking if we match the selected template while typing
            if (selectedTemplate.value.id && lastSelectedTemplate.value.id !== selectedTemplate.value.id) {
                lastSelectedTemplate.value = selectedTemplate.value;
            }

            // If we have no template set, but having a last selected template
            // check if the message is a match, if so, "reassign" the selected template again
            if (lastSelectedTemplate.value.id) {
                if ("content" in lastSelectedTemplate.value) {
                    const baseRegexString = escapeRegExp(lastSelectedTemplate.value.content);
                    const regex = new RegExp(baseRegexString.replace(/\\{\\{\s*([^}]*)\s*\\}\\}/g, ".*"));

                    // Test the last selected template, if we have a match on the message then we re-set the selected template
                    if (regex.test(props.chatEntryInput.message)) {
                        selectedTemplate.value = lastSelectedTemplate.value;
                        isTemplate.value = true;
                    } else {
                        if (Object.keys(selectedTemplate.value).length) {
                            resetTemplate();
                        }
                    }
                }
            }

            if (!props.typingInterval) {
                emit("send-websockets-message", startedWebsocketsPayload.value);
                emit(
                    "set-typing-interval",
                    setInterval(emit("send-websockets-message", startedWebsocketsPayload.value), 3000)
                );
            }

            clearTimeout(isTypingTimeout.value);
            isTypingTimeout.value = setTimeout(() => {
                clearInterval(props.typingInterval);
                emit("set-typing-interval", false);
                emit("send-websockets-message", stoppedWebsocketsPayload.value);
            }, 5000);
        };

        const checkEnterToSend = () => {
            if (enterToSend.value === "1") {
                if (isChatEnabledV21(props.activeChat)) {
                    emit("send-message");
                } else if (isTemplate.value && !selectedTemplate.value.mediaType) {
                    emit("send-message");
                } else if (
                    isTemplate.value &&
                    selectedTemplate.value.mediaType &&
                    hasMediaTemplateFilesUploaded.value
                ) {
                    emit("send-message");
                }
            }

            return false;
        };

        const checkShiftEnterToSend = () => {
            if (enterToSend.value === "0") {
                if (isChatEnabledV21(props.activeChat)) {
                    emit("send-message");
                } else if (isTemplate.value && !selectedTemplate.value.mediaType) {
                    emit("send-message");
                } else if (
                    isTemplate.value &&
                    selectedTemplate.value.mediaType &&
                    hasMediaTemplateFilesUploaded.value
                ) {
                    emit("send-message");
                }
            }

            return false;
        };

        const uploadFile = (event) => {
            const files = event.clipboardData?.files || event.dataTransfer?.files;

            if (files?.length > 0) {
                event.preventDefault();
                event.stopPropagation();

                emit("upload-files", Array.from(files));
            }
        };

        const allowedFileTypes = computed(() =>
            getAllowedFileTypes(selectedTemplate.value?.mediaType, props.activeChat.channel.platform.toLowerCase())
        );

        const onTyping = (newText) => {
            setTyping(newText);

            if (templateRegex.test(newText)) {
                templateSearchString.value = newText.substring(1);
            } else {
                templateSearchString.value = "";
            }

            toast.clear(); // Remove all existing toast before showing a new one.
            if (
                newText.length >=
                resolveWriteMessageMaxChars(props.activeChat.channel.platform, props.activeChat.channel.provider)
            ) {
                toast(
                    {
                        component: toastificationContent,
                        props: {
                            title: i18n.t("entities.conversation.Conversation"),
                            text: i18n.t("inbox.reached-character-limit", {
                                charLimit: resolveWriteMessageMaxChars(
                                    props.activeChat.channel.platform,
                                    props.activeChat.channel.provider
                                ),
                            }),
                            icon: "exclamation-triangle",
                            variant: "warning",
                        },
                    },
                    {
                        timeout: false,
                    }
                );
            }
        };

        const { setStoredMessages } = useStoredMessages();
        const { isLoading: isLoadingFiles } = useFileUploads();

        const showAutomaticSignature = (settingValue) =>
            !isTemplate.value &&
            !isQuickReply.value &&
            getUserData().displayName &&
            settingValue === getChannelSettingValue(props.activeChat.channel, "automaticSignature");

        // Replies
        const { activeReply, removeReply } = useReplies();

        return {
            channels,
            shallShowEmojiPicker,
            writeMessage,

            // Template stuff
            activeChatTemplatesTab,
            resetTemplate,
            selectedTemplate,
            isTemplate,
            isQuickReply,
            hasMediaTemplateFilesUploaded,

            isEnabled: computed(() => isChatEnabledV21(props.activeChat)),

            resolveCustomerCareWindow: computed(() =>
                resolveRemainingCustomerCareWindow(
                    props.activeChat.lastInboundMessageTime,
                    props.activeChat.channel?.customerCareWindowLength
                )
            ),
            resolveWriteMessageMaxChars: computed(() =>
                resolveWriteMessageMaxChars(props.activeChat.channel.platform, props.activeChat.channel.provider)
            ),

            isChatAssignedToMeV21,
            shouldAssignV21,

            enterToSend,
            onTyping,

            dropZoneRef,
            isOverDropZone,
            checkEnterToSend,
            checkShiftEnterToSend,

            inboxModuleType: InboxModuleType.MESSAGING,
            setStoredMessages,

            uploadFile,
            allowedFileTypes,

            activeTabIndex,

            isMobile,

            isLoadingFiles,

            toast,

            clearFiles,
            addAsset,

            getChannelSettingValue,
            getUserData,

            showAutomaticSignature,

            activeReply,
            removeReply,
        };
    },
};
</script>

<style lang="scss">
@import "~@/assets/scss/vEmojiPicker.scss";
</style>
